import { useState, useCallback, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { useLenis } from 'lenis/react';
import { mockGalleryImages } from '../../data/mockData';

const Gallery = () => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const lenis = useLenis();
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const lightboxRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const images = mockGalleryImages.sort((a, b) => a.order - b.order);

  const openLightbox = useCallback((index: number, opener: HTMLButtonElement) => {
    setCurrentImageIndex(index);
    openerRef.current = opener;
    setIsLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
  }, []);

  useEffect(() => {
    if (!isLightboxOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    lenis?.stop();
    closeButtonRef.current?.focus();

    const dialog = lightboxRef.current;
    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialog) return;
      const buttons = dialog.querySelectorAll<HTMLButtonElement>('button:not([disabled])');
      const firstButton = buttons[0];
      const lastButton = buttons[buttons.length - 1];

      if (event.shiftKey && document.activeElement === firstButton) {
        event.preventDefault();
        lastButton?.focus();
      } else if (!event.shiftKey && document.activeElement === lastButton) {
        event.preventDefault();
        firstButton?.focus();
      }
    };

    dialog?.addEventListener('keydown', handleTab);

    return () => {
      dialog?.removeEventListener('keydown', handleTab);
      document.body.style.overflow = previousOverflow;
      lenis?.start();
      // Focus back to the opener element with a small delay to ensure DOM is updated
      requestAnimationFrame(() => {
        openerRef.current?.focus();
      });
    };
  }, [isLightboxOpen, lenis]);

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isLightboxOpen) return;
    
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeLightbox();
        break;
      case 'ArrowRight':
        e.preventDefault();
        nextImage();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        prevImage();
        break;
    }
  }, [isLightboxOpen, closeLightbox, nextImage, prevImage]);

  // Add event listener for keyboard navigation
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const renderImage = (image: (typeof mockGalleryImages)[number], index: number) => (
    <button
      type="button"
      key={image.id}
      aria-label={`Open ${image.alt} in the image viewer`}
      aria-haspopup="dialog"
      className="group relative block w-full overflow-hidden rounded-lg bg-rizes-limestone text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rizes-olive focus-visible:ring-offset-2"
      style={{ animationDelay: `${index * 70}ms` } as React.CSSProperties}
      onClick={(event) => openLightbox(index, event.currentTarget)}
    >
      <div className="relative aspect-[3/2] overflow-hidden">
        <img
          src={image.thumbnail}
          alt={image.alt}
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.025] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          loading="lazy"
          decoding="async"
        />
        <div className="gallery-image-overlay pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between px-4 pb-4 text-white opacity-0 transition-[opacity,transform] duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100 motion-reduce:transition-none gallery-caption">
          <span className="font-medium">{image.alt}</span>
          <Maximize2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        </div>
      </div>
    </button>
  );

  return (
    <section id="gallery" className="bg-rizes-ivory py-14 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-8 flex flex-col gap-3 border-b border-rizes-limestone pb-6 sm:mb-10 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase text-rizes-olive">Rizes · Sifnos</p>
            <h2 className="section-title">Photo Gallery</h2>
          </div>
          <p className="section-subtitle mx-0 mt-0 max-w-md sm:text-right">
            Explore the house in Rizes, Sifnos.
          </p>
        </header>

        <div className="space-y-4 sm:space-y-5 lg:space-y-7">
          <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-[2fr_1fr] lg:gap-7">
            {images[0] && renderImage(images[0], 0)}
            <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-1 lg:gap-7">
              {images.slice(1, 3).map((image, index) => renderImage(image, index + 1))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 lg:gap-7">
            {images.slice(3, 6).map((image, index) => renderImage(image, index + 3))}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:gap-7">
            {images.slice(6, 8).map((image, index) => renderImage(image, index + 6))}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-5 xl:grid-cols-4 xl:gap-7">
            {images.slice(8, 12).map((image, index) => renderImage(image, index + 8))}
          </div>
        </div>

        {/* Lightbox Modal */}
        {isLightboxOpen && (
          <div ref={lightboxRef} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-label="Photo viewer">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              aria-label="Close photo viewer"
              className="absolute top-6 right-6 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors backdrop-blur-sm z-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              ref={closeButtonRef}
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Navigation Arrows */}
            <button
              onClick={prevImage}
              aria-label="Previous photo"
              className="absolute left-6 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors backdrop-blur-sm z-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            
            <button
              onClick={nextImage}
              aria-label="Next photo"
              className="absolute right-6 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors backdrop-blur-sm z-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>

            {/* Image Display */}
            <div className="max-w-4xl w-full max-h-[80vh] flex items-center justify-center">
              <img
                src={images[currentImageIndex].url}
                alt={images[currentImageIndex].alt}
                className="max-w-full max-h-[70vh] object-contain rounded-xl"
                decoding="async"
              />
            </div>

            {/* Image Counter */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white z-20">
              <span className="bg-black/50 px-4 py-2 rounded-full">
                {currentImageIndex + 1} / {images.length}
              </span>
            </div>

            {/* Image Title */}
            <div className="absolute bottom-6 left-6 text-white z-20">
              <p className="text-lg font-medium">{images[currentImageIndex].alt}</p>
            </div>

            {/* Click outside to close */}
            <div
              className="absolute inset-0 z-10"
              aria-hidden="true"
              onClick={closeLightbox}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
